import type { Prop } from '@cuboom/shared';

/**
 * Procedural prop art — trees, rocks, flowers, planters, bushes, street
 * lamps and crystal shards. Drawn from the same Prop record the sim owns
 * (ground anchor at p.y, body spans p.h upward, p.style colors, p.seed
 * shapes), so what the showcase page shows is exactly what ships in play.
 */

/** Stable 0..1 helpers derived from the prop's placement seed. */
function frac(seed: number, salt: number): number {
  let t = (seed ^ Math.imul(salt, 0x9e3779b9)) | 0;
  t = Math.imul(t ^ (t >>> 16), 0x45d9f3b);
  t = Math.imul(t ^ (t >>> 16), 0x45d9f3b);
  return ((t ^ (t >>> 16)) >>> 0) / 4294967296;
}

export function drawProp(ctx: CanvasRenderingContext2D, p: Prop, nowS: number): void {
  const { x, y, w, h, style } = p;
  switch (p.kind) {
    case 'tree': {
      // Gentle wind: the whole canopy rides a slow sway, trunk stays put.
      const sway = Math.sin(nowS * 1.1 + frac(p.seed, 1) * 6.28) * (w * 0.06);
      const hue = 118 + (style - 0.5) * 46; // per-placement green
      // Trunk with two branch stubs.
      ctx.fillStyle = '#6b4a2f';
      const tw = Math.max(6, w * 0.16);
      ctx.fillRect(x - tw / 2, y - h * 0.58, tw, h * 0.58);
      ctx.strokeStyle = '#6b4a2f';
      ctx.lineWidth = Math.max(3, tw * 0.55);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y - h * 0.42);
      ctx.lineTo(x - w * 0.18, y - h * 0.55);
      ctx.moveTo(x, y - h * 0.48);
      ctx.lineTo(x + w * 0.2, y - h * 0.6);
      ctx.stroke();
      // Canopy: three lobes, darker underbelly then a sunlit cap.
      const cy = y - h * 0.68 + sway * 0.4;
      const r = w * 0.56;
      ctx.fillStyle = `hsl(${hue}, 42%, 34%)`;
      ctx.beginPath();
      ctx.arc(x - r * 0.5 + sway, cy + r * 0.18, r * 0.72, 0, Math.PI * 2);
      ctx.arc(x + r * 0.55 + sway, cy + r * 0.22, r * 0.66, 0, Math.PI * 2);
      ctx.arc(x + sway, cy - r * 0.1, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `hsl(${hue}, 48%, 46%)`;
      ctx.beginPath();
      ctx.arc(x - r * 0.3 + sway, cy - r * 0.3, r * 0.62, 0, Math.PI * 2);
      ctx.arc(x + r * 0.35 + sway, cy - r * 0.15, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      // Leaf glints.
      ctx.fillStyle = `hsl(${hue}, 52%, 58%)`;
      ctx.beginPath();
      ctx.arc(x - r * 0.15 + sway, cy - r * 0.45, r * 0.2, 0, Math.PI * 2);
      ctx.arc(x + r * 0.45 + sway, cy - r * 0.35, r * 0.14, 0, Math.PI * 2);
      ctx.fill();
      break;
    }

    case 'rock': {
      // Faceted stone: hexagon split into lit and shaded halves.
      const cxr = x;
      const cyr = y - h / 2;
      const n = 6;
      const pts: [number, number][] = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const rr = (i % 2 ? 0.88 : 1) * (0.85 + frac(p.seed, i + 2) * 0.3);
        pts.push([cxr + Math.cos(a) * (w / 2) * rr, cyr + Math.sin(a) * (h / 2) * rr]);
      }
      ctx.fillStyle = '#7d8188';
      ctx.beginPath();
      pts.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
      ctx.closePath();
      ctx.fill();
      // Lit top-left facet, shaded base.
      ctx.fillStyle = '#a4a9b0';
      ctx.beginPath();
      ctx.moveTo(pts[5][0], pts[5][1]);
      ctx.lineTo(pts[0][0], pts[0][1]);
      ctx.lineTo(pts[1][0], pts[1][1]);
      ctx.lineTo(cxr, cyr);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(40,44,52,.45)';
      ctx.beginPath();
      ctx.moveTo(pts[2][0], pts[2][1]);
      ctx.lineTo(pts[3][0], pts[3][1]);
      ctx.lineTo(pts[4][0], pts[4][1]);
      ctx.lineTo(cxr, cyr);
      ctx.closePath();
      ctx.fill();
      break;
    }

    case 'flower': {
      // Curved stem with two leaves, five petals around a bright eye.
      const tipY = y - h;
      ctx.strokeStyle = '#3f9147';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + (style - 0.5) * 8, y - h * 0.55, x, tipY);
      ctx.stroke();
      ctx.fillStyle = '#3f9147';
      ctx.beginPath();
      ctx.ellipse(x - 4, y - h * 0.4, 5, 2.4, -0.5, 0, Math.PI * 2);
      ctx.ellipse(x + 4, y - h * 0.6, 5, 2.4, 0.5, 0, Math.PI * 2);
      ctx.fill();
      const hue = Math.floor(style * 360);
      ctx.fillStyle = `hsl(${hue}, 80%, 62%)`;
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + nowS * 0.15;
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * 4.4, tipY + Math.sin(a) * 4.4, 3.4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = '#ffe08a';
      ctx.beginPath();
      ctx.arc(x, tipY, 2.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }

    case 'pot': {
      // Terracotta planter with a rim and a little sprout.
      ctx.fillStyle = '#b35a37';
      ctx.beginPath();
      ctx.moveTo(x - w * 0.36, y - h * 0.78);
      ctx.lineTo(x + w * 0.36, y - h * 0.78);
      ctx.lineTo(x + w * 0.28, y);
      ctx.lineTo(x - w * 0.28, y);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#8f4529'; // shaded right side
      ctx.beginPath();
      ctx.moveTo(x + w * 0.1, y - h * 0.78);
      ctx.lineTo(x + w * 0.36, y - h * 0.78);
      ctx.lineTo(x + w * 0.28, y);
      ctx.lineTo(x + w * 0.08, y);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#c96a44'; // rim
      ctx.fillRect(x - w * 0.42, y - h * 0.86, w * 0.84, h * 0.12);
      ctx.fillStyle = '#4aa95e';
      ctx.beginPath();
      ctx.ellipse(x - 3, y - h * 0.95, 5, 3, -0.6, 0, Math.PI * 2);
      ctx.ellipse(x + 3, y - h, 5, 3, 0.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }

    case 'bush': {
      // Three-lobed shrub with a sunlit crown and a few berries.
      const hue = 128 + (style - 0.5) * 36;
      const br = h * 0.55;
      ctx.fillStyle = `hsl(${hue}, 40%, 32%)`;
      ctx.beginPath();
      ctx.ellipse(x - w * 0.26, y - br * 0.7, w * 0.3, br * 0.72, 0, 0, Math.PI * 2);
      ctx.ellipse(x + w * 0.26, y - br * 0.66, w * 0.3, br * 0.68, 0, 0, Math.PI * 2);
      ctx.ellipse(x, y - br * 0.9, w * 0.36, br * 0.9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `hsl(${hue}, 46%, 44%)`;
      ctx.beginPath();
      ctx.ellipse(x - w * 0.08, y - br * 1.15, w * 0.26, br * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `hsl(${(style * 360 + 40) % 360}, 75%, 62%)`;
      ctx.beginPath();
      ctx.arc(x - w * 0.2, y - br * 0.85, 2.2, 0, Math.PI * 2);
      ctx.arc(x + w * 0.16, y - br * 1.05, 2.2, 0, Math.PI * 2);
      ctx.arc(x + w * 0.28, y - br * 0.6, 2.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }

    case 'lamp': {
      // Path lamp: dark post, curved arm, warm head that softly pulses.
      const headY = y - h;
      const glow = 0.75 + Math.sin(nowS * 2 + frac(p.seed, 7) * 6.28) * 0.15;
      ctx.strokeStyle = '#2f3540';
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, headY + 6);
      ctx.quadraticCurveTo(x, headY, x + 7, headY);
      ctx.stroke();
      // Warm halo behind the head.
      const grad = ctx.createRadialGradient(x + 9, headY + 4, 1, x + 9, headY + 4, 16);
      grad.addColorStop(0, `rgba(255, 224, 138, ${0.55 * glow})`);
      grad.addColorStop(1, 'rgba(255, 224, 138, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x + 9, headY + 4, 16, 0, Math.PI * 2);
      ctx.fill();
      // Lantern hood + bulb.
      ctx.fillStyle = '#2f3540';
      ctx.beginPath();
      ctx.moveTo(x + 3, headY + 1);
      ctx.lineTo(x + 13, headY + 1);
      ctx.lineTo(x + 11, headY + 5);
      ctx.lineTo(x + 5, headY + 5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = `rgba(255, 236, 170, ${glow})`;
      ctx.beginPath();
      ctx.arc(x + 9, headY + 7, 3, 0, Math.PI * 2);
      ctx.fill();
      // Base plate.
      ctx.fillStyle = '#2f3540';
      ctx.fillRect(x - 4, y - 4, 8, 4);
      break;
    }

    case 'crystal': {
      // Faceted shard that catches the light — hue from placement, slow pulse.
      const hue = Math.floor(style * 360);
      const cxp = x;
      const cyp = y - h / 2;
      const pulse = 0.82 + Math.sin(nowS * 1.6 + frac(p.seed, 9) * 6.28) * 0.18;
      // Soft ground glow.
      const glow = ctx.createRadialGradient(cxp, cyp, 2, cxp, cyp, h * 0.75);
      glow.addColorStop(0, `hsla(${hue}, 80%, 65%, ${0.28 * pulse})`);
      glow.addColorStop(1, `hsla(${hue}, 80%, 65%, 0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cxp, cyp, h * 0.75, 0, Math.PI * 2);
      ctx.fill();
      const topX = cxp;
      const topY = y - h;
      const leftX = cxp - w / 2;
      const rightX = cxp + w / 2;
      const midY = y - h * 0.38;
      const botX = cxp + (frac(p.seed, 3) - 0.5) * w * 0.3;
      // Left facet (lit).
      ctx.fillStyle = `hsl(${hue}, 72%, ${62 * pulse}%)`;
      ctx.beginPath();
      ctx.moveTo(topX, topY);
      ctx.lineTo(leftX, midY);
      ctx.lineTo(botX, y);
      ctx.closePath();
      ctx.fill();
      // Right facet (shaded).
      ctx.fillStyle = `hsl(${hue}, 70%, ${44 * pulse}%)`;
      ctx.beginPath();
      ctx.moveTo(topX, topY);
      ctx.lineTo(rightX, midY);
      ctx.lineTo(botX, y);
      ctx.closePath();
      ctx.fill();
      // Inner highlight streak.
      ctx.fillStyle = `hsla(${hue}, 90%, 88%, ${0.5 * pulse})`;
      ctx.beginPath();
      ctx.moveTo(topX, topY);
      ctx.lineTo(cxp - w * 0.12, midY);
      ctx.lineTo(cxp + w * 0.04, y - h * 0.1);
      ctx.closePath();
      ctx.fill();
      break;
    }
  }
}
